import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Form,
  Modal,
  Offcanvas,
  Spinner,
} from "react-bootstrap";
import {
  AlertTriangle,
  Ban,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Columns3,
  FileSpreadsheet,
  Languages,
  Mail,
  Package,
  PackageCheck,
  Pencil,
  RefreshCcw,
  Search,
  Send,
  Truck,
  X,
} from "lucide-react";
import NavbarUtility from "./NavbarUtility";

const GOLD = "#ebb51b";

const API_URL =
  "https://dashboard.technoparts.it/ws/controllo_ordini_fornitori.php";
  
const DOC_TYPES = [
  "TUTTE",
  "OF",
  "OFU",
  "OF2",
  "OFU2",
  "OF2G",
  "OF3",
  "OFU3",
  "OF3G",
  "OF4",
  "OFU4",
  "OF4G",
];

const DEPOSITS = ["TUTTI", "01", "02", "03", "04"];

const MAIL_ACTIONS = {
  conf_it: {
    label: "Conferma IT",
    subject: "Richiesta conferma ordini",
  },
  conf_en: {
    label: "Confirmation EN",
    subject: "Order Confirmation Request",
  },
  modifica: {
    label: "Modifica ordine",
    subject: "ORDER MODIFICATION / MODIFICA ORDINE",
  },
  cancella: {
    label: "Cancella ordine",
    subject: "ORDER CANCELLATION / CANCELLAZIONE ORDINE",
  },
  eta: {
    label: "Update ETA",
    subject: "UPDATE ETA ORDER",
  },
};

const pad = (n) => String(n).padStart(2, "0");

const toISO = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const plusDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const todayISO = () => toISO(new Date());
const daysAgoISO = (days) => toISO(plusDays(new Date(), -days));

const formatDate = (value) => {
  if (!value || value === "(NULL)" || value === "null") return "—";
  const raw = String(value).split(" ")[0].split("T")[0];
  const [y, m, d] = raw.split("-").map(Number);
  if (!y || !m || !d) return value;
  return `${pad(d)}/${pad(m)}/${y}`;
};

const formatMoney = (value) => {
  const n = Number(String(value ?? 0).replace(",", "."));
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const numberValue = (value) => {
  const n = Number(String(value ?? 0).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const rowKey = (row) => {
  if (row?.__key) return row.__key;
  if (row?.["Tipo Riga"] === "D" && row?.["ID Ordine"] != null) {
    return [
      "D",
      row["ID Ordine"],
      row["ID cliente"] || "",
      row["Tipo Doc Cli"] || "",
      row["Num Doc Cli"] || "",
    ].join("-");
  }
  return [
    row?.["Tipo Riga"] || "R",
    row?.Anno || "",
    row?.["ID Fornitore"] || "",
    row?.["Tipo Doc"] || "",
    row?.["Num Doc"] || "",
  ].join("-");
};

const sameSupplierOrder = (a, b) =>
  String(a?.Anno ?? "") === String(b?.Anno ?? "") &&
  String(a?.["ID Fornitore"] ?? "") === String(b?.["ID Fornitore"] ?? "") &&
  String(a?.["Tipo Doc"] ?? "") === String(b?.["Tipo Doc"] ?? "") &&
  String(a?.["Num Doc"] ?? "") === String(b?.["Num Doc"] ?? "");

const orderGroupKey = (row) =>
  [
    row?.Anno ?? "",
    row?.["ID Fornitore"] ?? "",
    row?.["Tipo Doc"] ?? "",
    row?.["Num Doc"] ?? "",
  ].join("|");

const makeDefaultFilters = () => ({
  dateFrom: daysAgoISO(7),
  dateTo: todayISO(),
  tipoDoc: "OF4G",
  deposito: "TUTTI",
  fornitore: "",
  ricerca: "",
  numeroOrdine: "",
  precodice: "",
  articolo: "",
  confermati: false,
  daConfermare: true,
  nonConfermati: false,
  evasi: false,
  daEvadere: false,
  bem: false,
  view: "CRUSCOTTO",
});

const styles = `
  :root {
    --co-gold: #ebb51b;
    --co-gold-soft: rgba(235,181,27,.13);
    --co-panel: rgba(18,20,25,.72);
    --co-border: rgba(255,255,255,.095);
    --co-border-soft: rgba(255,255,255,.055);
    --co-text: #f5f6f7;
    --co-muted: #9298a2;
    --co-petrol: rgba(41, 73, 88, .72);
  }

  .co-page {
    height: 100dvh;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    color: var(--co-text);
    background:
      radial-gradient(circle at 8% 3%, rgba(235,181,27,.09), transparent 24%),
      radial-gradient(circle at 90% 18%, rgba(89,104,126,.10), transparent 28%),
      linear-gradient(145deg,#090a0d 0%,#101218 50%,#08090c 100%);
  }

  .co-content {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
    overflow: hidden;
    padding: 5px 10px 7px;
  }

  .co-glass {
    position: relative;
    background: linear-gradient(145deg, rgba(255,255,255,.055), rgba(255,255,255,.018)), var(--co-panel);
    border: 1px solid var(--co-border);
    box-shadow: 0 14px 38px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.07);
    backdrop-filter: blur(18px) saturate(145%);
    -webkit-backdrop-filter: blur(18px) saturate(145%);
  }

  .co-toolbar {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 28px;
  }

  .co-view-tabs,
  .co-actions {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-wrap: nowrap;
  }

  .co-btn {
    min-height: 30px;
    border-radius: 8px !important;
    padding: 0 10px !important;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: .76rem !important;
    font-weight: 760 !important;
    white-space: nowrap;
    border: 1px solid rgba(255,255,255,.10) !important;
    transition: background .15s ease, border-color .15s ease, transform .15s ease !important;
  }

  .co-btn:hover:not(:disabled) { transform: translateY(-1px); }

  .co-btn-primary,
  .co-tab.active {
    color: #17130a !important;
    background: #ebb51b !important;
    border-color: rgba(255,220,105,.46) !important;
    box-shadow: 0 7px 18px rgba(235,181,27,.14);
  }

  .co-btn-ghost {
    color: #dfe2e6 !important;
    background: rgba(255,255,255,.04) !important;
  }

  .co-btn-ghost:hover:not(:disabled) {
    color: #fff !important;
    background: rgba(255,255,255,.075) !important;
    border-color: rgba(235,181,27,.22) !important;
  }

  .co-btn-danger {
    color: #ffd1d1 !important;
    background: rgba(174,48,48,.14) !important;
    border-color: rgba(255,99,99,.18) !important;
  }

  .co-filter-panel {
    flex: 0 0 auto;
    border-radius: 14px;
    padding: 8px 9px;
  }

  .co-filter-main {
    display: grid;
    grid-template-columns: minmax(245px,.95fr) minmax(180px,.72fr) minmax(230px,.86fr) minmax(650px,1.7fr);
    gap: 8px;
    align-items: start;
  }

  .co-filter-block {
    min-width: 0;
    display: grid;
    gap: 5px;
  }

  .co-stack-2 { grid-template-rows: repeat(2, 32px); }

  .co-inline-field {
    min-width: 0;
    display: grid;
    grid-template-columns: 30px minmax(0,1fr);
    gap: 6px;
    align-items: center;
  }

  .co-inline-field.co-doc-field {
    grid-template-columns: 58px minmax(0,1fr);
  }

  .co-label {
    color: #b9bec6;
    font-size: .72rem;
    font-weight: 800;
    line-height: 1;
    letter-spacing: .06em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .co-control,
  .co-control.form-control,
  .co-control.form-select {
    width: 100%;
    min-height: 30px;
    height: 32px;
    padding: 4px 9px !important;
    color: #f3f4f5 !important;
    background: rgba(255,255,255,.045) !important;
    border: 1px solid rgba(255,255,255,.085) !important;
    border-radius: 8px !important;
    box-shadow: none !important;
    font-size: .82rem !important;
    color-scheme: dark;
  }

  .co-control:focus {
    border-color: rgba(235,181,27,.48) !important;
    box-shadow: 0 0 0 2px rgba(235,181,27,.08) !important;
  }

  .co-control::placeholder { color: #656b74 !important; }
  select.co-control option { color:#111; background:#f2f2f2; }

  .co-quick-grid {
    display: grid;
    grid-template-columns: repeat(2,minmax(0,1fr));
    grid-template-rows: repeat(2,32px);
    gap: 6px;
  }

  .co-quick-grid .co-btn {
    width: 100%;
    min-height: 24px;
    padding: 0 6px !important;
  }

  .co-deposit-wrap {
    min-width: 0;
    display: grid;
    grid-template-columns: 58px minmax(0,1fr);
    gap: 6px;
    align-items: center;
  }

  .co-deposit-grid {
    min-width: 0;
    display: grid;
    grid-template-columns: 1.25fr repeat(4, .72fr);
    gap: 3px;
  }

  .co-deposit-btn {
    min-width: 0 !important;
    min-height: 32px !important;
    padding: 0 5px !important;
    font-size: .74rem !important;
  }

  .co-deposit-btn.active {
    color: #17130a !important;
    background: #ebb51b !important;
    border-color: rgba(255,220,105,.45) !important;
  }

  .co-search-grid {
    min-width: 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0,1fr));
    grid-template-rows: repeat(2, 44px);
    column-gap: 8px;
    row-gap: 5px;
    align-items: start;
  }

  .co-search-field {
    min-width: 0;
    display: grid;
    grid-template-rows: 10px 30px;
    gap: 4px;
  }

  /* Riga 1: Fornitore | Numero ordine | Ricerca note */
  .co-search-field.fornitore { grid-column: 1; grid-row: 1; }
  .co-search-field.numero { grid-column: 2; grid-row: 1; }
  .co-search-field.note { grid-column: 3; grid-row: 1; }

  /* Riga 2: stessa larghezza input, allineati a destra */
  .co-search-field.precodice { grid-column: 2; grid-row: 2; }
  .co-search-field.articolo { grid-column: 3; grid-row: 2; }

  .co-search-grid .co-label {
    font-size: .68rem;
    line-height: 10px;
  }

  .co-search-grid .co-control,
  .co-search-grid .co-control.form-control {
    height: 30px;
    min-height: 30px;
    font-size: .82rem !important;
  }

  .co-checks {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-wrap: nowrap;
    margin-top: 5px;
    padding-top: 5px;
    border-top: 1px solid var(--co-border-soft);
    overflow-x: auto;
    scrollbar-width: none;
  }

  .co-checks::-webkit-scrollbar { display:none; }

  .co-check-chip {
    min-height: 29px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 0 8px;
    border-radius: 8px;
    color: #cfd3d8;
    background: rgba(255,255,255,.03);
    border: 1px solid rgba(255,255,255,.065);
    font-size: .76rem;
    font-weight: 690;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
  }

  .co-check-chip:has(input:checked) {
    color: #f7e4a5;
    background: rgba(235,181,27,.095);
    border-color: rgba(235,181,27,.22);
  }

  .co-check-chip input {
    width: 13px;
    height: 13px;
    margin: 0;
    accent-color: var(--co-gold);
  }

  .co-notice {
    flex: 0 0 auto;
    padding: 6px 9px;
    border-radius: 9px;
    font-size: .68rem;
    border: 1px solid rgba(255,255,255,.08);
    background: rgba(255,255,255,.04);
  }

  .co-notice.success { color:#a6efbd; border-color:rgba(72,199,116,.18); background:rgba(72,199,116,.08); }
  .co-notice.warn { color:#ffe18b; border-color:rgba(235,181,27,.2); background:rgba(235,181,27,.08); }

  .co-table-panel {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 14px;
  }

  .co-table-head {
    flex: 0 0 31px;
    min-height: 31px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 0 10px;
    border-bottom: 1px solid var(--co-border-soft);
    background: rgba(255,255,255,.014);
  }

  .co-table-title {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: .82rem;
    font-weight: 780;
  }

  .co-count {
    min-width: 28px;
    height: 21px;
    padding: 0 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    color: #17130a;
    background: #ebb51b;
    font-size: .76rem;
    font-weight: 850;
  }

  .co-table-wrap {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    overscroll-behavior: contain;
    scrollbar-width: thin;
    scrollbar-color: rgba(235,181,27,.56) rgba(255,255,255,.025);
  }

  .co-table-wrap::-webkit-scrollbar { width: 8px; height: 8px; }
  .co-table-wrap::-webkit-scrollbar-track { background: rgba(255,255,255,.02); }
  .co-table-wrap::-webkit-scrollbar-thumb {
    background: rgba(235,181,27,.46);
    border: 2px solid rgba(12,14,18,.92);
    border-radius: 999px;
  }
  .co-table-wrap::-webkit-scrollbar-thumb:hover { background: rgba(235,181,27,.72); }
  .co-table-wrap::-webkit-scrollbar-corner { background: transparent; }

  .co-table {
    width: max-content;
    min-width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    color: #e2e4e7;
    font-size: .76rem;
  }

  .co-table th {
    position: sticky;
    top: 0;
    z-index: 6;
    height: 34px;
    padding: 5px 7px;
    color: #b9bec6;
    background: rgba(15,17,21,.985);
    border-bottom: 1px solid rgba(255,255,255,.09);
    border-right: 1px solid rgba(255,255,255,.03);
    font-size: .68rem;
    font-weight: 800;
    letter-spacing: .045em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .co-table td {
    height: 32px;
    padding: 4px 7px;
    color: #dde0e4;
    background: rgba(255,255,255,.01);
    border-bottom: 1px solid rgba(255,255,255,.038);
    border-right: 1px solid rgba(255,255,255,.026);
    white-space: nowrap;
  }

  .co-table tbody tr { transition: background .13s ease; }
  .co-table tbody tr:hover td { background: rgba(255,255,255,.038); }

  .co-row-head td {
    color: #f4f7f8 !important;
    background: var(--co-petrol) !important;
    border-top: 1px solid rgba(116,180,204,.16) !important;
    border-bottom: 1px solid rgba(116,180,204,.17) !important;
    font-weight: 680;
  }

  .co-row-head td:first-child { box-shadow: inset 3px 0 0 var(--co-gold); }
  .co-row-head:hover td { background: rgba(49,85,101,.82) !important; }
  .co-row-selected td { background: rgba(235,181,27,.075) !important; }

  .co-row-warning td[data-danger="true"] {
    color: #ff8585;
    font-weight: 760;
  }

  .co-edit-input {
    min-width: 100px;
    height: 27px;
    color: #f4f5f6;
    background: rgba(255,255,255,.038);
    border: 1px solid rgba(255,255,255,.065);
    border-radius: 6px;
    padding: 0 6px;
    font-size: .76rem;
    outline: none;
    color-scheme: dark;
  }

  .co-edit-input:focus {
    border-color: rgba(235,181,27,.45);
    box-shadow: 0 0 0 2px rgba(235,181,27,.07);
  }

  .co-edit-text { min-width: 118px; }

  .co-note-link {
    display: inline-block;
    max-width: 125px;
    padding: 0;
    border: 0;
    color: #d8dce1;
    background: transparent;
    font: inherit;
    text-align: left;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }

  .co-note-link:hover { color: #f0c84b; text-decoration: underline; }
  .co-note-link.empty { color: #717781; text-decoration: none; cursor: default; }

  .co-empty,
  .co-loading,
  .co-error {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 18px;
    text-align: center;
    color: var(--co-muted);
  }

  .co-loading strong,
  .co-error strong { color:#f3f4f5; font-size:.82rem; }
  .co-loading .spinner-border { color:var(--co-gold); }

  .co-error-icon {
    width: 38px;
    height: 38px;
    display:grid;
    place-items:center;
    border-radius:12px;
    color:#ff8d8d;
    background:rgba(187,55,55,.13);
    border:1px solid rgba(255,100,100,.18);
  }

  .co-offcanvas {
    color:#f3f4f5 !important;
    background: radial-gradient(circle at 18% 0%,rgba(235,181,27,.12),transparent 30%), linear-gradient(155deg,#101217,#171a20 58%,#0b0d10) !important;
    border-left:1px solid rgba(255,255,255,.1) !important;
  }
  .co-offcanvas .btn-close { filter:invert(1); opacity:.75; }
  .co-offcanvas .offcanvas-header { border-bottom:1px solid rgba(255,255,255,.06); }
  .co-column-option {
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:12px;
    padding:11px 0;
    border-bottom:1px solid rgba(255,255,255,.055);
  }
  .co-column-option small { display:block; color:#8f949e; margin-top:2px; }
  .co-column-option input { accent-color:var(--co-gold); }

  .co-mail-modal .modal-content,
  .co-note-modal .modal-content {
    color:#eff1f3;
    background: radial-gradient(circle at 8% 0%,rgba(235,181,27,.10),transparent 28%), linear-gradient(145deg,#0d0f13,#15181e 60%,#0a0c0f);
    border:1px solid rgba(255,255,255,.11);
    border-radius:18px;
    box-shadow:0 24px 80px rgba(0,0,0,.55),inset 0 1px 0 rgba(255,255,255,.08);
  }

  .co-mail-modal .modal-header,
  .co-mail-modal .modal-footer,
  .co-note-modal .modal-header { border-color:rgba(255,255,255,.07); }
  .co-mail-modal .btn-close,
  .co-note-modal .btn-close { filter:invert(1); }

  .co-mail-preview {
    padding:12px;
    border-radius:12px;
    background:rgba(255,255,255,.032);
    border:1px solid rgba(255,255,255,.065);
  }
  .co-mail-preview label {
    display:block;
    margin-bottom:4px;
    color:#9197a1;
    font-size:.62rem;
    font-weight:800;
    text-transform:uppercase;
    letter-spacing:.06em;
  }
  .co-mail-body {
    margin-top:9px;
    min-height:160px;
    white-space:pre-wrap;
    color:#d7d9de;
    font-size:.76rem;
    line-height:1.45;
  }

  .co-note-modal-body {
    white-space: pre-wrap;
    color:#e2e4e8;
    font-size:.9rem;
    line-height:1.5;
  }

  @media (max-height: 820px) and (min-width: 1081px) {
    .co-content { padding: 5px 9px 6px; gap: 5px; }
    .co-toolbar { min-height: 30px; }
    .co-btn { min-height: 29px; font-size: .72rem !important; }
    .co-filter-panel { padding: 7px 8px; }
    .co-control,
    .co-control.form-control,
    .co-control.form-select { min-height: 30px; height: 30px; font-size: .78rem !important; }
    .co-stack-2 { grid-template-rows: repeat(2,30px); }
    .co-quick-grid { grid-template-rows: repeat(2,30px); }
    .co-quick-grid .co-btn,
    .co-deposit-btn { min-height: 30px !important; }
    .co-search-grid { grid-template-rows: repeat(2,42px); }
    .co-search-field { grid-template-rows: 10px 28px; }
    .co-search-grid .co-control,
    .co-search-grid .co-control.form-control { height:28px; min-height:28px; }
    .co-checks { margin-top: 5px; padding-top: 5px; }
    .co-check-chip { min-height: 28px; font-size: .72rem; }
    .co-table-head { flex-basis: 32px; min-height: 32px; }
    .co-table { font-size: .75rem; }
    .co-table th { height: 32px; padding: 4px 7px; font-size: .66rem; }
    .co-table td { height: 30px; padding: 4px 7px; }
    .co-edit-input { height: 25px; font-size: .72rem; }
  }

  @media (max-width: 1250px) {
    .co-filter-main { grid-template-columns: 225px 175px 220px minmax(590px,1fr); gap:6px; }
    .co-btn { padding:0 8px !important; font-size:.72rem !important; }
    .co-actions { gap:4px; }
    .co-search-grid { column-gap:6px; }
  }

  @media (max-width: 1080px) {
    .co-page { height:auto; min-height:100vh; overflow:auto; }
    .co-content { overflow:visible; }
    .co-toolbar { align-items:flex-start; flex-wrap:wrap; }
    .co-view-tabs,.co-actions { flex-wrap:wrap; }
    .co-filter-main { grid-template-columns: repeat(2,minmax(0,1fr)); }
    .co-search-grid { grid-column: 1 / -1; }
    .co-table-panel { min-height:520px; }
    .co-table-wrap { max-height:62vh; }
  }

  @media (max-width: 767px) {
    .co-content { padding:7px; }
    .co-filter-panel,.co-table-panel { border-radius:13px; }
    .co-filter-main { grid-template-columns:1fr; }
    .co-search-grid {
      grid-template-columns:repeat(2,minmax(0,1fr));
      grid-template-rows:auto;
    }
    .co-search-field.fornitore,
    .co-search-field.note,
    .co-search-field.numero,
    .co-search-field.precodice,
    .co-search-field.articolo { grid-column:auto; grid-row:auto; }
    .co-search-field.note { grid-column:1 / -1; }
    .co-table-panel { min-height:560px; }
  }

  /* =========================
   FILTRI - COMPACT LAYOUT
========================= */

.co-filter-panel {
  flex: 0 0 auto;
  border-radius: 14px;
  padding: 9px 12px;
}

/* I 4 blocchi principali non occupano più tutto lo spazio */
.co-filter-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  width: 100%;
}

/* DATE */
.co-filter-block {
  min-width: 0;
  display: grid;
  gap: 5px;
  flex: 0 0 auto;
}

.co-stack-2 {
  grid-template-rows: repeat(2, 32px);
}

.co-inline-field {
  display: grid;
  grid-template-columns: 30px 145px;
  gap: 6px;
  align-items: center;
}

.co-inline-field.co-doc-field {
  grid-template-columns: 58px 110px;
}

/* LABEL */
.co-label {
  color: #b9bec6;
  font-size: .68rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: .05em;
  text-transform: uppercase;
  white-space: nowrap;
}

/* INPUT GENERALI */
.co-control,
.co-control.form-control,
.co-control.form-select {
  min-height: 30px;
  height: 30px;
  padding: 3px 8px !important;
  color: #f3f4f5 !important;
  background: rgba(255,255,255,.045) !important;
  border: 1px solid rgba(255,255,255,.085) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  font-size: .78rem !important;
  color-scheme: dark;
}

.co-control:focus {
  border-color: rgba(235,181,27,.48) !important;
  box-shadow: 0 0 0 2px rgba(235,181,27,.08) !important;
}

.co-control::placeholder {
  color: #656b74 !important;
}

select.co-control option {
  color: #111;
  background: #f2f2f2;
}


/* =========================
   OGGI / SETTIMANA / MESE / ANNO
========================= */

.co-quick-grid {
  flex: 0 0 175px;
  display: grid;
  grid-template-columns: repeat(2, 82px);
  grid-template-rows: repeat(2, 30px);
  gap: 6px;
}

.co-quick-grid .co-btn {
  width: 82px;
  min-height: 30px;
  padding: 0 6px !important;
}


/* =========================
   DOCUMENTO + DEPOSITO
========================= */

.co-deposit-wrap {
  display: grid;
  grid-template-columns: 58px auto;
  gap: 6px;
  align-items: center;
}

.co-deposit-grid {
  display: flex;
  align-items: center;
  gap: 3px;
}

.co-deposit-btn {
  width: auto !important;
  min-width: 35px !important;
  min-height: 30px !important;
  padding: 0 6px !important;
  font-size: .70rem !important;
}

.co-deposit-grid .co-deposit-btn:first-child {
  min-width: 52px !important;
}

.co-deposit-btn.active {
  color: #17130a !important;
  background: #ebb51b !important;
  border-color: rgba(255,220,105,.45) !important;
}


/* =========================
   CAMPI DI RICERCA
========================= */

.co-search-grid {
  flex: 0 0 auto;

  display: grid;

  /*
    Prima riga:
    Fornitore | Numero ordine | Ricerca note

    Seconda:
                Precodice     | Articolo
  */
  grid-template-columns: 180px 135px 180px;
  grid-template-rows: repeat(2, 42px);

  column-gap: 14px;
  row-gap: 6px;

  align-items: start;
}

/* Ogni campo NON prende più il 100% del pannello */
.co-search-field {
  min-width: 0;
  display: grid;
  grid-template-rows: 9px 29px;
  gap: 4px;
}

.co-search-field.fornitore {
  grid-column: 1;
  grid-row: 1;
}

.co-search-field.numero {
  grid-column: 2;
  grid-row: 1;
}

.co-search-field.note {
  grid-column: 3;
  grid-row: 1;
}

.co-search-field.precodice {
  grid-column: 2;
  grid-row: 2;
}

.co-search-field.articolo {
  grid-column: 3;
  grid-row: 2;
}

.co-search-grid .co-label {
  font-size: .64rem;
  line-height: 9px;
}

.co-search-grid .co-control,
.co-search-grid .co-control.form-control {
  width: 100%;
  height: 29px;
  min-height: 29px;
  font-size: .76rem !important;
}


/* =========================
   CHECKBOX SOTTO
========================= */

.co-checks {
  display: flex;
  align-items: center;
  gap: 8px;

  margin-top: 7px;
  padding-top: 7px;

  border-top: 1px solid var(--co-border-soft);
}

.co-check-chip {
  min-height: 28px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 5px;
  padding: 0 9px;

  border-radius: 8px;

  color: #cfd3d8;
  background: rgba(255,255,255,.03);
  border: 1px solid rgba(255,255,255,.065);

  font-size: .72rem;
  font-weight: 690;

  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.co-check-chip:has(input:checked) {
  color: #f7e4a5;
  background: rgba(235,181,27,.095);
  border-color: rgba(235,181,27,.22);
}

.co-check-chip input {
  width: 13px;
  height: 13px;
  margin: 0;
  accent-color: var(--co-gold);
}


/* =========================
   RESPONSIVE
========================= */

@media (max-width: 1250px) {
  .co-filter-main {
    gap: 10px;
  }

  .co-search-grid {
    grid-template-columns: 160px 120px 160px;
    column-gap: 8px;
  }

  .co-inline-field {
    grid-template-columns: 30px 125px;
  }
}

@media (max-width: 1080px) {
  .co-filter-main {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .co-search-grid {
    grid-column: 1 / -1;
    width: 100%;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .co-checks {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}

@media (max-width: 767px) {
  .co-filter-main {
    grid-template-columns: 1fr;
  }

  .co-search-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: auto;
  }

  .co-search-field.fornitore,
  .co-search-field.numero,
  .co-search-field.note,
  .co-search-field.precodice,
  .co-search-field.articolo {
    grid-column: auto;
    grid-row: auto;
  }

  .co-search-field.note {
    grid-column: 1 / -1;
  }

  .co-inline-field {
    grid-template-columns: 35px minmax(0, 160px);
  }

  .co-checks {
    justify-content: flex-start;
  }
}

/* ========================================
   FILTRI DESKTOP - COMPATTI E RAGGRUPPATI
======================================== */

.co-filter-panel {
  flex: 0 0 auto;
  border-radius: 14px;
  padding: 8px 12px;
}


/* Tutto parte da sinistra, niente mega spazio centrale */
.co-filter-main {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 18px;
  width: 100%;
}


/* =========================
   1. DATE
========================= */

.co-filter-block {
  min-width: 0;
  flex: 0 0 auto;
  display: grid;
  gap: 5px;
}

.co-stack-2 {
  grid-template-rows: repeat(2, 32px);
}

.co-inline-field {
  display: grid;
  grid-template-columns: 30px 145px;
  gap: 7px;
  align-items: center;
}


/* =========================
   2. OGGI / SETTIMANA...
========================= */

.co-quick-grid {
  flex: 0 0 auto;

  display: grid;
  grid-template-columns: repeat(2, 82px);
  grid-template-rows: repeat(2, 32px);

  gap: 5px;
}

.co-quick-grid .co-btn {
  width: 82px;
  min-height: 32px;
  padding: 0 7px !important;
}


/* =========================
   DIVISORE
   viene messo prima del blocco DOC/DEPOSITO
========================= */

.co-filter-main > .co-filter-block:nth-child(3) {
  position: relative;

  margin-left: 12px;
  padding-left: 30px;
}

.co-filter-main > .co-filter-block:nth-child(3)::before {
  content: "";

  position: absolute;
  left: 0;
  top: 3px;
  bottom: 3px;

  width: 1px;

  background: rgba(255,255,255,.12);
}


/* =========================
   3. DOC
========================= */

.co-inline-field.co-doc-field {
  display: grid;

  /* DOC | SELECT */
  grid-template-columns: 58px 110px;

  gap: 8px;
  align-items: center;
}


/* =========================
   DEPOSITO
========================= */

.co-deposit-wrap {
  display: flex;
  align-items: center;

  /* IMPORTANTE:
     label + bottoni tutti sulla stessa riga */
  flex-wrap: nowrap;

  gap: 8px;

  min-width: max-content;
}


/* Deposito NON deve andare sopra */
.co-deposit-wrap > .co-label {
  flex: 0 0 58px;
  width: 58px;

  margin: 0;

  white-space: nowrap;
}


/* TUTTI 01 02 03 04 sempre in linea */
.co-deposit-grid {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;

  gap: 4px;

  width: auto;
  min-width: max-content;
}


.co-deposit-btn {
  flex: 0 0 auto;

  width: 37px !important;
  min-width: 37px !important;

  height: 30px !important;
  min-height: 30px !important;

  padding: 0 5px !important;

  font-size: .70rem !important;
}


/* TUTTI leggermente più largo */
.co-deposit-grid .co-deposit-btn:first-child {
  width: 52px !important;
  min-width: 52px !important;
}


.co-deposit-btn.active {
  color: #17130a !important;
  background: #ebb51b !important;
  border-color: rgba(255,220,105,.45) !important;
}


/* =========================
   INPUT
========================= */

.co-control,
.co-control.form-control,
.co-control.form-select {
  min-height: 30px;
  height: 30px;

  padding: 3px 8px !important;

  color: #f3f4f5 !important;

  background: rgba(255,255,255,.045) !important;
  border: 1px solid rgba(255,255,255,.085) !important;

  border-radius: 8px !important;

  box-shadow: none !important;

  font-size: .78rem !important;

  color-scheme: dark;
}


/* =========================
   RICERCHE
========================= */

.co-search-grid {
  flex: 0 0 auto;

  display: grid;

  grid-template-columns:
    175px
    130px
    175px;

  grid-template-rows:
    repeat(2, 42px);

  column-gap: 10px;
  row-gap: 5px;

  align-items: start;

  /*
    Altro piccolo divisore prima
    delle ricerche
  */
  position: relative;

  margin-left: 12px;
  padding-left: 30px;
}


.co-search-grid::before {
  content: "";

  position: absolute;

  left: 0;
  top: 3px;
  bottom: 3px;

  width: 1px;

  background: rgba(255,255,255,.12);
}


.co-search-field {
  min-width: 0;

  display: grid;

  grid-template-rows:
    9px
    29px;

  gap: 4px;
}


.co-search-field.fornitore {
  grid-column: 1;
  grid-row: 1;
}

.co-search-field.numero {
  grid-column: 2;
  grid-row: 1;
}

.co-search-field.note {
  grid-column: 3;
  grid-row: 1;
}

.co-search-field.precodice {
  grid-column: 2;
  grid-row: 2;
}

.co-search-field.articolo {
  grid-column: 3;
  grid-row: 2;
}


.co-search-grid .co-control,
.co-search-grid .co-control.form-control {
  width: 100%;

  height: 29px;
  min-height: 29px;

  font-size: .76rem !important;
}


/* =========================
   LABEL
========================= */

.co-label {

  color: #b9bec6;

  font-size: .68rem;
  font-weight: 800;

  line-height: 1;

  letter-spacing: .05em;

  text-transform: uppercase;

  white-space: nowrap;
}


/* =========================
   RESPONSIVE
========================= */

@media (max-width: 1250px) {

  .co-filter-main {
    gap: 10px;
  }

  .co-filter-main > .co-filter-block:nth-child(3),
  .co-search-grid {
    margin-left: 4px;
    padding-left: 16px;
  }

  .co-search-grid {
    grid-template-columns:
      150px
      115px
      150px;

    column-gap: 7px;
  }
}


@media (max-width: 1080px) {

  .co-filter-main {
    display: grid;

    grid-template-columns:
      repeat(2, minmax(0, 1fr));

    gap: 10px;
  }

  .co-filter-main > .co-filter-block:nth-child(3) {
    margin-left: 0;
    padding-left: 0;
  }

  .co-filter-main > .co-filter-block:nth-child(3)::before {
    display: none;
  }

  .co-search-grid {
    grid-column: 1 / -1;

    width: 100%;

    margin-left: 0;
    padding-left: 0;

    grid-template-columns:
      repeat(3, minmax(0, 1fr));
  }

  .co-search-grid::before {
    display: none;
  }
}


@media (max-width: 767px) {

  .co-filter-main {
    grid-template-columns: 1fr;
  }

  .co-search-grid {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));

    grid-template-rows: auto;
  }

  .co-search-field.fornitore,
  .co-search-field.numero,
  .co-search-field.note,
  .co-search-field.precodice,
  .co-search-field.articolo {
    grid-column: auto;
    grid-row: auto;
  }

  .co-search-field.note {
    grid-column: 1 / -1;
  }

  .co-deposit-wrap {
    justify-content: flex-start;
  }
}
  
.co-search-grid {
  flex: 0 0 auto;

  display: grid;
  grid-template-columns: 175px 130px 175px;
  grid-template-rows: repeat(2, 42px);

  column-gap: 10px;
  row-gap: 5px;

  align-items: start;

  /* SPINGE TUTTO IL BLOCCO A DESTRA */
  margin-left: auto;

  /* divisore prima degli input */
  position: relative;
  padding-left: 28px;
}

.co-search-grid::before {
  content: "";

  position: absolute;
  left: 8px;
  top: 3px;
  bottom: 3px;

  width: 1px;

  background: rgba(255,255,255,.12);
}

.co-btn.co-btn-ghost.co-deposit-btn.active.btn.btn-primary, .co-controlform-select {
margin-left: 10px !important;
  }
`;


export function ControlloOrdiniFornitori() {
  const [filters, setFilters] = useState(makeDefaultFilters);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);

  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [showColumns, setShowColumns] = useState(false);
  const [columnGroups, setColumnGroups] = useState({
    anno: false,
    supplier: true,
    destination: false,
    client: false,
  });

  const [mailPreview, setMailPreview] = useState(null);
  const [noteModal, setNoteModal] = useState(null);

  const updateFilter = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const setQuickRange = (days) => {
    const now = new Date();
    const start = days === 0 ? now : plusDays(now, -days);
    setFilters((prev) => ({
      ...prev,
      dateFrom: toISO(start),
      dateTo: toISO(now),
    }));
  };

  // Il backend riceve SOLO il periodo. Tutti gli altri filtri lavorano in React.
  const loadOrders = async (nextFilters = filters) => {
    if (!nextFilters.dateFrom || !nextFilters.dateTo) return;
    if (nextFilters.dateFrom > nextFilters.dateTo) {
      setError("La data iniziale non può essere successiva alla data finale.");
      return;
    }

    setLoading(true);
    setError("");
    setNotice(null);
    setSelectedKeys(new Set());

    try {
      const params = new URLSearchParams({
        data_inizio: nextFilters.dateFrom,
        data_fine: nextFilters.dateTo,
        _ts: String(Date.now()),
      });

      const response = await fetch(`${API_URL}?${params.toString()}`, {
        cache: "no-store",
      });

      const text = await response.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch {
        throw new Error("Il PHP non ha restituito JSON valido.");
      }

      if (!response.ok || json?.success === false) {
        throw new Error(json?.error || `Errore HTTP ${response.status}`);
      }

      const list = Array.isArray(json?.data) ? json.data : [];
      setRows(list.map((row, index) => ({
        ...row,
        __key: rowKey(row) || `row-${index}`,
      })));
    } catch (e) {
      console.error("Errore caricamento controllo ordini:", e);
      setRows([]);
      setError(e?.message || "Errore durante il caricamento degli ordini.");
    } finally {
      setLoading(false);
    }
  };

  // Solo le date fanno una nuova query. I filtri sotto sono istantanei lato React.
  useEffect(() => {
    if (!filters.dateFrom || !filters.dateTo || filters.dateFrom > filters.dateTo) return undefined;
    const timer = window.setTimeout(() => {
      loadOrders({ dateFrom: filters.dateFrom, dateTo: filters.dateTo });
    }, 220);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.dateFrom, filters.dateTo]);

  const groupedRows = useMemo(() => {
    const headers = new Map();
    const details = new Map();

    rows.forEach((row) => {
      const key = orderGroupKey(row);
      if (row["Tipo Riga"] === "T") {
        headers.set(key, row);
      } else if (row["Tipo Riga"] === "D") {
        if (!details.has(key)) details.set(key, []);
        details.get(key).push(row);
      }
    });

    return { headers, details };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const supplierNeedle = filters.fornitore.trim().toLowerCase();
    const noteNeedle = filters.ricerca.trim().toLowerCase();
    const orderNeedle = filters.numeroOrdine.trim();
    const precodiceNeedle = filters.precodice.trim().toLowerCase();
    const articleNeedle = filters.articolo.trim().toLowerCase();

    const supplierIsNumeric = supplierNeedle !== "" && /^\d+$/.test(supplierNeedle);

    const passesHeaderFilters = (row) => {
      if (filters.tipoDoc !== "TUTTE" && String(row["Tipo Doc"] || "") !== filters.tipoDoc) {
        return false;
      }

      if (supplierNeedle) {
        if (supplierIsNumeric) {
          if (String(row["ID Fornitore"] ?? "") !== supplierNeedle) return false;
        } else if (!String(row["Ragione sociale"] || "").toLowerCase().includes(supplierNeedle)) {
          return false;
        }
      }

      if (noteNeedle && !String(row["Note documento"] || "").toLowerCase().includes(noteNeedle)) {
        return false;
      }

      if (orderNeedle && String(row["Num Doc"] ?? "") !== orderNeedle) {
        return false;
      }

      // Nel gestionale Confermati/Da confermare controllano le date della TESTATA.
      const header = groupedRows.headers.get(orderGroupKey(row)) || row;
      const hasConfirmation = Boolean(header?.["Data Conferma"] || header?.["Data Effettiva"]);

      // Se entrambi sono spuntati, come nel C#, non viene applicato alcun filtro.
      if (!(filters.confermati && filters.daConfermare)) {
        if (filters.confermati && !hasConfirmation) return false;
        if (filters.daConfermare && hasConfirmation) return false;
      }

      return true;
    };

    const passesDetailFilters = (row) => {
      if (filters.deposito !== "TUTTI" && String(row.Deposito || "") !== filters.deposito) {
        return false;
      }

      if (precodiceNeedle && String(row.Precodice || "").toLowerCase() !== precodiceNeedle) {
        return false;
      }

      if (articleNeedle && !String(row.Articolo || "").toLowerCase().includes(articleNeedle)) {
        return false;
      }

      // NON CONFERMATI è solo dettaglio; in vista TESTATE viene ignorato come nel WinForms.
      if (filters.nonConfermati && filters.view !== "TESTATE") {
        if (!(numberValue(row["Qta DEV"]) === 0 && numberValue(row["Qta EVA"]) < numberValue(row.Quantita))) {
          return false;
        }
      }

      if (filters.evasi && numberValue(row["Qta EVA"]) !== numberValue(row.Quantita)) {
        return false;
      }

      if (filters.daEvadere && numberValue(row["Qta DEV"]) <= 0) {
        return false;
      }

      if (filters.bem && !row.BEM) {
        return false;
      }

      return true;
    };

    const detailFilterActive =
      filters.deposito !== "TUTTI" ||
      Boolean(precodiceNeedle) ||
      Boolean(articleNeedle) ||
      filters.evasi ||
      filters.daEvadere ||
      filters.bem ||
      (filters.nonConfermati && filters.view !== "TESTATE");

    return rows.filter((row) => {
      if (!passesHeaderFilters(row)) return false;

      if (row["Tipo Riga"] === "D") {
        return passesDetailFilters(row);
      }

      if (row["Tipo Riga"] === "T" && detailFilterActive) {
        const details = groupedRows.details.get(orderGroupKey(row)) || [];
        return details.some((detail) => passesDetailFilters(detail));
      }

      return true;
    });
  }, [rows, filters, groupedRows]);

  const displayRows = useMemo(() => {
    let visible = filteredRows;

    if (filters.view === "TESTATE") {
      visible = filteredRows.filter((row) => row["Tipo Riga"] === "T");
    } else if (filters.view === "DETTAGLIO") {
      visible = filteredRows.filter((row) => row["Tipo Riga"] === "D");
    }

    return [...visible].sort((a, b) => {
      const numA = String(a?.["Num Doc"] ?? "");
      const numB = String(b?.["Num Doc"] ?? "");
      const byNumDoc = numA.localeCompare(numB, "it", { numeric: true, sensitivity: "base" });
      if (byNumDoc !== 0) return byNumDoc;

      const typeA = a?.["Tipo Riga"] === "T" ? 0 : 1;
      const typeB = b?.["Tipo Riga"] === "T" ? 0 : 1;
      if (typeA !== typeB) return typeA - typeB;

      return String(a?.["ID Ordine"] ?? "").localeCompare(
        String(b?.["ID Ordine"] ?? ""),
        "it",
        { numeric: true, sensitivity: "base" },
      );
    });
  }, [filteredRows, filters.view]);

  const selectedRows = useMemo(
    () => filteredRows.filter((row) => selectedKeys.has(rowKey(row))),
    [filteredRows, selectedKeys],
  );

  const selectedDetails = useMemo(
    () => selectedRows.filter((row) => row["Tipo Riga"] === "D"),
    [selectedRows],
  );

  const toggleSelection = (row) => {
    const key = rowKey(row);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const nextChecked = !next.has(key);

      if (row["Tipo Riga"] === "T") {
        filteredRows.forEach((candidate) => {
          if (candidate["Tipo Riga"] === "D" && sameSupplierOrder(row, candidate)) {
            const detailKey = rowKey(candidate);
            if (nextChecked) next.add(detailKey);
            else next.delete(detailKey);
          }
        });
      }

      if (nextChecked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const editCell = (row, column, value) => {
    const key = rowKey(row);
    setRows((prev) =>
      prev.map((item) => (rowKey(item) === key ? { ...item, [column]: value } : item)),
    );
    setNotice({
      type: "warn",
      text: "Modifica aggiornata nel frontend. Il salvataggio nel database lo colleghiamo al PHP nel prossimo step.",
    });
  };

  const openMailPreview = (type) => {
    if (selectedDetails.length === 0) {
      setNotice({ type: "warn", text: "Seleziona almeno una riga dettaglio prima di preparare la mail." });
      return;
    }

    const action = MAIL_ACTIONS[type];
    const first = selectedDetails[0];
    const supplier = first["Ragione sociale"] || "Fornitore";
    const client = first["Rag Soc Cli"] || "Cliente";
    const orderNumbers = [...new Set(selectedDetails.map((row) => row["Num Doc"]).filter(Boolean))];
    const clientOrders = [...new Set(selectedDetails.map((row) => row["Num Doc Cli"]).filter(Boolean))];

    let subject = action.subject;
    let body = "";

    if (type === "conf_it") {
      subject = `${first["Tipo Doc"] === "OF4G" ? "[URGENTE] " : ""}Richiesta conferma ordini`;
      body = `Buongiorno ${supplier},\n\npotreste per favore confermarci la ricezione e la lavorazione dei seguenti ordini?\n\n${selectedDetails
        .map((r) => `• Ordine ${r["Num Doc"]} — ${r.Articolo || ""} — Qta ${numberValue(r.Quantita)}`)
        .join("\n")}\n\nQuando è prevista la consegna?\n\nGrazie mille`;
    } else if (type === "conf_en") {
      subject = `${first["Tipo Doc"] === "OF4G" ? "[URGENT] " : ""}Order Confirmation Request`;
      body = `Dear ${supplier},\n\nwe kindly ask you to confirm receipt and processing of the following orders.\n\n${selectedDetails
        .map((r) => `• Order ${r["Num Doc"]} — ${r.Articolo || ""} — Qty ${numberValue(r.Quantita)}`)
        .join("\n")}\n\nWhen is delivery expected?\n\nThanks in advance`;
    } else if (type === "modifica") {
      subject = `ORDER MODIFICATION / MODIFICA ORDINE ${clientOrders.join(", ") || orderNumbers.join(", ")}`;
      body = `Dear ${client},\n\nplease be informed of the following modification.\n\n${selectedDetails
        .map((r) => `• ${r.Articolo || ""} — Qty ${numberValue(r.Quantita)} — New Qty: ...`)
        .join("\n")}\n\n---\n\nBuongiorno ${client},\n\nvi informiamo delle seguenti modifiche relative al vostro ordine.\n\nBest regards / Cordiali saluti`;
    } else if (type === "cancella") {
      subject = `ORDER CANCELLATION / CANCELLAZIONE ORDINE ${clientOrders.join(", ") || orderNumbers.join(", ")}`;
      body = `Dear ${client},\n\nplease be informed of the following cancellation.\n\n${selectedDetails
        .map((r) => `• ${r.Articolo || ""} — Qty ${numberValue(r.Quantita)}`)
        .join("\n")}\n\n---\n\nBuongiorno ${client},\n\nvi informiamo delle seguenti cancellazioni relative al vostro ordine.\n\nBest regards / Cordiali saluti`;
    } else {
      subject = `UPDATE ETA ORDER ${clientOrders.join(", ") || orderNumbers.join(", ")}`;
      body = `Dear ${client},\n\nplease be informed that ETA for the following references has been updated.\n\n${selectedDetails
        .map((r) => {
          const base = r["Data Effettiva"] || r["Data Conferma"];
          const newEta = base ? formatDate(toISO(plusDays(new Date(base), 3))) : "...";
          return `• ${r.Articolo || ""} — Qty ${numberValue(r.Quantita)} — New ETA ${newEta}`;
        })
        .join("\n")}\n\n---\n\nBuongiorno ${client},\n\nvi informiamo che la data di consegna prevista è stata aggiornata.\n\nBest regards / Cordiali saluti`;
    }

    setMailPreview({ type, title: action.label, subject, body });
  };

  const exportCsv = () => {
    if (displayRows.length === 0) {
      setNotice({ type: "warn", text: "Non ci sono righe da esportare." });
      return;
    }

    const columns = [
      "Tipo Riga",
      "Anno",
      "ID Fornitore",
      "Ragione sociale",
      "Data Ordine",
      "Tipo Doc",
      "Num Doc",
      "Rif. Fornitore",
      "Note documento",
      "Data Conferma",
      "Data Effettiva",
      "Deposito",
      "Precodice",
      "Articolo",
      "Descrizione",
      "Quantita",
      "Qta EVA",
      "Qta DEV",
      "Qta Rimesso",
      "BEM",
      "Netto",
      "Importo",
      "ID cliente",
      "Rag Soc Cli",
      "Tipo Doc Cli",
      "Num Doc Cli",
      "Rif. Cliente",
    ];

    const esc = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
    const csv = [
      columns.map(esc).join(";"),
      ...displayRows.map((row) => columns.map((col) => esc(row[col])).join(";")),
    ].join("\r\n");

    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `controllo_ordini_${todayISO()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const renderEditableText = (row, column, allow) => {
    if (!allow) return row[column] || "—";
    return (
      <input
        className="co-edit-input co-edit-text"
        value={row[column] || ""}
        placeholder="—"
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => editCell(row, column, e.target.value)}
      />
    );
  };

  const renderEditableDate = (row, column) => (
    <input
      type="date"
      className="co-edit-input"
      value={String(row[column] || "").split(" ")[0].split("T")[0]}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => editCell(row, column, e.target.value)}
    />
  );

  return (
    <div className="co-page w-100">
      <style>{styles}</style>
      <NavbarUtility />

      <main className="co-content">
        <div className="co-toolbar">
          <div className="co-view-tabs">
            {[
              ["CRUSCOTTO", "Cruscotto", ClipboardList],
              ["TESTATE", "Testate", Truck],
              ["DETTAGLIO", "Dettaglio", Package],
            ].map(([value, label, Icon]) => (
              <Button
                key={value}
                className={`co-btn co-btn-ghost co-tab ${filters.view === value ? "active" : ""}`}
                onClick={() => updateFilter("view", value)}
              >
                <Icon size={13} /> {label}
              </Button>
            ))}

            <Button className="co-btn co-btn-ghost" onClick={() => setShowColumns(true)}>
              <Columns3 size={13} /> Colonne
            </Button>
          </div>

          <div className="co-actions">
            <Button className="co-btn co-btn-ghost" onClick={exportCsv}>
              <FileSpreadsheet size={13} /> Esporta
            </Button>
            <Button className="co-btn co-btn-ghost" onClick={() => openMailPreview("conf_it")}>
              <Mail size={13} /> Conferma IT
            </Button>
            <Button className="co-btn co-btn-ghost" onClick={() => openMailPreview("conf_en")}>
              <Languages size={13} /> Confirmation EN
            </Button>
            <Button className="co-btn co-btn-ghost" onClick={() => openMailPreview("modifica")}>
              <Pencil size={13} /> Modifica
            </Button>
            <Button className="co-btn co-btn-danger" onClick={() => openMailPreview("cancella")}>
              <Ban size={13} /> Cancella
            </Button>
            <Button className="co-btn co-btn-ghost" onClick={() => openMailPreview("eta")}>
              <CalendarDays size={13} /> ETA
            </Button>
          </div>
        </div>

        <section className="co-glass co-filter-panel">
          <div className="co-filter-main">
            <div className="co-filter-block co-stack-2">
              <div className="co-inline-field">
                <label className="co-label">Dal</label>
                <Form.Control
                  type="date"
                  className="co-control"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilter("dateFrom", e.target.value)}
                />
              </div>
              <div className="co-inline-field">
                <label className="co-label">Al</label>
                <Form.Control
                  type="date"
                  className="co-control"
                  min={filters.dateFrom}
                  value={filters.dateTo}
                  onChange={(e) => updateFilter("dateTo", e.target.value)}
                />
              </div>
            </div>

            <div className="co-quick-grid">
              {[
                [0, "Oggi"],
                [7, "Settimana"],
                [30, "Mese"],
                [365, "Anno"],
              ].map(([days, label]) => (
                <Button key={label} className="co-btn co-btn-ghost" onClick={() => setQuickRange(days)}>
                  {label}
                </Button>
              ))}
            </div>

            <div className="co-filter-block co-stack-2">
              <div className="co-inline-field co-doc-field">
                <label className="co-label">Doc</label>
                <Form.Select
                  className="co-control"
                  value={filters.tipoDoc}
                  onChange={(e) => updateFilter("tipoDoc", e.target.value)}
                >
                  {DOC_TYPES.map((item) => <option key={item}>{item}</option>)}
                </Form.Select>
              </div>

              <div className="co-deposit-wrap">
                <label className="co-label">Deposito</label>
                <div className="co-deposit-grid">
                  {DEPOSITS.map((item) => (
                    <Button
                      key={item}
                      className={`co-btn co-btn-ghost co-deposit-btn ${filters.deposito === item ? "active" : ""}`}
                      onClick={() => updateFilter("deposito", item)}
                    >
                      {item}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <div className="co-search-grid">
              <div className="co-search-field fornitore">
                <label className="co-label">Fornitore</label>
                <Form.Control
                  className="co-control"
                  placeholder="Codice o ragione sociale"
                  value={filters.fornitore}
                  onChange={(e) => updateFilter("fornitore", e.target.value)}
                />
              </div>

              <div className="co-search-field numero">
                <label className="co-label">Numero ordine</label>
                <Form.Control
                  className="co-control"
                  placeholder="Es. 14332"
                  value={filters.numeroOrdine}
                  onChange={(e) => updateFilter("numeroOrdine", e.target.value)}
                />
              </div>

              <div className="co-search-field note">
                <label className="co-label">Ricerca nelle note</label>
                <Form.Control
                  className="co-control"
                  placeholder="Testo nota"
                  value={filters.ricerca}
                  onChange={(e) => updateFilter("ricerca", e.target.value)}
                />
              </div>

              <div className="co-search-field precodice">
                <label className="co-label">Precodice</label>
                <Form.Control
                  className="co-control"
                  placeholder="Es. 050"
                  value={filters.precodice}
                  onChange={(e) => updateFilter("precodice", e.target.value)}
                />
              </div>

              <div className="co-search-field articolo">
                <label className="co-label">Articolo</label>
                <Form.Control
                  className="co-control"
                  placeholder="Codice articolo"
                  value={filters.articolo}
                  onChange={(e) => updateFilter("articolo", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="co-checks">
            {[
              ["confermati", "Confermati", CheckCircle2],
              ["daConfermare", "Da confermare", Clock3],
              ["nonConfermati", "Non confermati", AlertTriangle],
              ["evasi", "Evasi", PackageCheck],
              ["daEvadere", "Da evadere", Package],
              ["bem", "BEM", Truck],
            ].map(([name, label, Icon]) => (
              <label className="co-check-chip" key={name}>
                <input
                  type="checkbox"
                  checked={Boolean(filters[name])}
                  onChange={(e) => updateFilter(name, e.target.checked)}
                />
                <Icon size={12} /> {label}
              </label>
            ))}
          </div>
        </section>

        {notice && <div className={`co-notice ${notice.type || ""}`}>{notice.text}</div>}

        <section className="co-glass co-table-panel">
          <div className="co-table-head">
            <h2 className="co-table-title">
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: GOLD, boxShadow: "0 0 12px rgba(235,181,27,.5)" }} />
              Ordini fornitori
            </h2>
            <span className="co-count">{displayRows.length}</span>
          </div>

          {loading ? (
            <div className="co-loading">
              <Spinner animation="border" size="sm" />
              <strong>Caricamento ordini</strong>
              <span>{formatDate(filters.dateFrom)} → {formatDate(filters.dateTo)}</span>
            </div>
          ) : error ? (
            <div className="co-error">
              <div className="co-error-icon"><AlertTriangle size={18} /></div>
              <strong>Errore durante il caricamento</strong>
              <span>{error}</span>
              <Button className="co-btn co-btn-primary" onClick={() => loadOrders()}>
                <RefreshCcw size={13} /> Riprova
              </Button>
            </div>
          ) : displayRows.length === 0 ? (
            <div className="co-empty">
              <Search size={21} />
              <strong style={{ color: "#f2f3f4" }}>Nessun ordine trovato</strong>
              <span>I filtri React non trovano righe nel periodo caricato.</span>
            </div>
          ) : (
            <div className="co-table-wrap">
              <table className="co-table">
                <thead>
                  <tr>
                    <th>Sel</th>
                    {columnGroups.anno && <th>Anno</th>}
                    {columnGroups.supplier && <><th>ID Fornitore</th><th>Ragione sociale</th></>}
                    {columnGroups.destination && <><th>ID Dest</th><th>Destinazione</th></>}
                    <th>Data Ordine</th>
                    <th>Tipo Doc</th>
                    <th>Num Doc</th>
                    <th>Rif. Fornitore</th>
                    <th>Note documento</th>
                    <th>Data Conferma</th>
                    <th>Data Effettiva</th>
                    <th>Deposito</th>
                    <th>Precodice</th>
                    <th>Articolo</th>
                    <th>Descrizione</th>
                    <th>Qta</th>
                    <th>Qta EVA</th>
                    <th>Qta DEV</th>
                    <th>Qta Rimesso</th>
                    <th>BEM</th>
                    <th>Netto</th>
                    <th>Importo</th>
                    {columnGroups.client && <><th>ID Cliente</th><th>Rag Soc Cli</th><th>Tipo Doc Cli</th><th>Num Doc Cli</th><th>Rif. Cliente</th></>}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row) => {
                    const key = rowKey(row);
                    const isHead = row["Tipo Riga"] === "T";
                    const isSelected = selectedKeys.has(key);
                    const warning = !isHead && numberValue(row["Qta DEV"]) === 0 && numberValue(row["Qta EVA"]) < numberValue(row.Quantita);
                    const note = String(row["Note documento"] || "").trim();

                    return (
                      <tr
                        key={key}
                        className={`${isHead ? "co-row-head" : ""} ${isSelected ? "co-row-selected" : ""} ${warning ? "co-row-warning" : ""}`}
                      >
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelection(row)}
                            style={{ accentColor: GOLD }}
                          />
                        </td>
                        {columnGroups.anno && <td>{row.Anno || "—"}</td>}
                        {columnGroups.supplier && <><td>{row["ID Fornitore"] || "—"}</td><td><strong>{row["Ragione sociale"] || "—"}</strong></td></>}
                        {columnGroups.destination && <><td>{row["ID Dest"] || "—"}</td><td>{row.Destinazione || "—"}</td></>}
                        <td>{formatDate(row["Data Ordine"])}</td>
                        <td><strong>{row["Tipo Doc"] || "—"}</strong></td>
                        <td><strong>{row["Num Doc"] || "—"}</strong></td>
                        <td>{renderEditableText(row, "Rif. Fornitore", isHead)}</td>
                        <td>
                          <button
                            type="button"
                            className={`co-note-link ${note ? "" : "empty"}`}
                            title={note || "Nessuna nota"}
                            onClick={() => note && setNoteModal({
                              note,
                              numDoc: row["Num Doc"],
                              tipoDoc: row["Tipo Doc"],
                            })}
                          >
                            {note || "—"}
                          </button>
                        </td>
                        <td>{renderEditableDate(row, "Data Conferma")}</td>
                        <td>{renderEditableDate(row, "Data Effettiva")}</td>
                        <td>{row.Deposito || "—"}</td>
                        <td>{row.Precodice || "—"}</td>
                        <td data-danger={warning ? "true" : undefined}><strong>{row.Articolo || "—"}</strong></td>
                        <td>{row.Descrizione || "—"}</td>
                        <td data-danger={warning ? "true" : undefined}>{numberValue(row.Quantita)}</td>
                        <td data-danger={warning ? "true" : undefined}>{numberValue(row["Qta EVA"])}</td>
                        <td data-danger={warning ? "true" : undefined}>{numberValue(row["Qta DEV"])}</td>
                        <td>{numberValue(row["Qta Rimesso"])}</td>
                        <td>{row.BEM ? <Badge bg="success">{row.BEM}</Badge> : "—"}</td>
                        <td>{row.Netto == null ? "—" : `€ ${formatMoney(row.Netto)}`}</td>
                        <td><strong>€ {formatMoney(row.Importo)}</strong></td>
                        {columnGroups.client && <><td>{row["ID cliente"] || "—"}</td><td>{row["Rag Soc Cli"] || "—"}</td><td>{row["Tipo Doc Cli"] || "—"}</td><td>{row["Num Doc Cli"] || "—"}</td><td>{row["Rif. Cliente"] || "—"}</td></>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      <Offcanvas
        placement="end"
        show={showColumns}
        onHide={() => setShowColumns(false)}
        className="co-offcanvas"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="d-flex align-items-center gap-2">
            <Columns3 size={18} color={GOLD} /> Colonne visibili
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {[
            ["anno", "Anno", "Mostra la colonna anno documento"],
            ["supplier", "Dati Fornitore", "ID fornitore e ragione sociale"],
            ["destination", "Dati Destinazione", "ID destinazione e descrizione"],
            ["client", "Dati Ordine Cliente", "Cliente, documento e riferimento cliente"],
          ].map(([key, label, description]) => (
            <label className="co-column-option" key={key}>
              <div>
                <strong>{label}</strong>
                <small>{description}</small>
              </div>
              <Form.Check
                type="switch"
                checked={columnGroups[key]}
                onChange={(e) => setColumnGroups((prev) => ({ ...prev, [key]: e.target.checked }))}
              />
            </label>
          ))}
        </Offcanvas.Body>
      </Offcanvas>

      <Modal
        show={Boolean(noteModal)}
        onHide={() => setNoteModal(null)}
        centered
        size="sm"
        className="co-note-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: ".86rem" }}>
            Nota {noteModal?.tipoDoc || ""} {noteModal?.numDoc || ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="co-note-modal-body">
            {noteModal?.note || "Nessuna nota."}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(mailPreview)}
        onHide={() => setMailPreview(null)}
        centered
        size="lg"
        className="co-mail-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: "1rem" }}>
            <Mail size={18} color={GOLD} />
            {mailPreview?.title || "Anteprima email"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="co-mail-preview">
            <label>Oggetto</label>
            <div className="fw-bold">{mailPreview?.subject}</div>
            <div className="co-mail-body">{mailPreview?.body}</div>
          </div>
          <div className="mt-3 d-flex gap-2 align-items-start" style={{ color: "#a4a8b0", fontSize: ".75rem" }}>
            <AlertTriangle size={15} color={GOLD} className="flex-shrink-0 mt-1" />
            <span>
              Questa è solo l'anteprima React. Il pulsante di invio verrà collegato dopo al PHP con la configurazione SMTP/app-password.
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="co-btn co-btn-ghost" onClick={() => setMailPreview(null)}>
            <X size={14} /> Chiudi
          </Button>
          <Button className="co-btn co-btn-primary" disabled>
            <Send size={14} /> Invio PHP nel prossimo step
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ControlloOrdiniFornitori;